import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { baseTranslations, loadTranslationsForRoute, getRouteKey } from '@/translations';

type Language = 'en' | 'pt';
type TranslationRecord = Record<string, string>;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang');
    return (langParam === 'en' || langParam === 'pt') ? langParam : 'pt';
  });

  // Start with base translations (common + home) - loaded synchronously
  const [translations, setTranslations] = useState<{ en: TranslationRecord; pt: TranslationRecord }>(baseTranslations);
  const [loadedRoutes, setLoadedRoutes] = useState<Set<string>>(new Set());

  // Load route-specific translations when pathname changes
  useEffect(() => {
    const pathname = window.location.pathname;
    const routeKey = getRouteKey(pathname);
    
    // Skip if already loaded or no route-specific translations needed
    if (!routeKey || loadedRoutes.has(routeKey)) return;

    loadTranslationsForRoute(pathname).then((routeTranslations) => {
      if (routeTranslations) {
        setTranslations(prev => ({
          en: { ...prev.en, ...routeTranslations.en },
          pt: { ...prev.pt, ...routeTranslations.pt },
        }));
        setLoadedRoutes(prev => new Set([...prev, routeKey]));
      }
    });
  }, [loadedRoutes]);

  const changeLanguage = (newLang: Language) => {
    setLanguageState(newLang);
    
    // Update URL parameter
    const url = new URL(window.location.href);
    url.searchParams.set('lang', newLang);
    window.history.replaceState({}, '', url.toString());
    
    // Update HTML lang attribute
    document.documentElement.lang = newLang === 'pt' ? 'pt-MZ' : 'en';
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
